import { Component, Input } from '@angular/core';

import { ReportVisualization, ReportWidgetData } from '../../../services/report-query';
import { treemapLayout } from './treemap-layout';

interface ChartPoint {
  category: string;
  value: number;
}

interface ChartSeries {
  name: string;
  points: ChartPoint[];
}

interface BarDatum extends ChartPoint {
  series: string;
  color: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface DonutSlice {
  label: string;
  value: number;
  color: string;
  start: number;
  end: number;
}

@Component({
  selector: 'app-report-chart',
  standalone: false,
  templateUrl: './report-chart.html',
  styleUrl: './report-chart.scss',
  host: {'[class.treemap-chart]': "visualization === 'treemap'"},
})
export class ReportChart {
  @Input({ required: true }) data!: ReportWidgetData;
  @Input() visualization: ReportVisualization = 'line';
  @Input() colors: string[] = ['#206bc4', '#2fb344', '#f59f00', '#d63939', '#ae3ec9'];
  @Input() currencyCode = 'USD';
  @Input() locale = 'en-US';

  readonly chartWidth = 640;
  readonly chartHeight = 220;
  readonly topPadding = 12;
  readonly bottomPadding = 28;

  get treemapTiles() {
    const { category, series, series_by } = this.data.verification.bindings;
    if (!category || series.length !== 1) return [];
    return treemapLayout(this.data.result.rows.map(row => ({
      label: String(row[category] ?? ''),
      value: this.toNumber(row[series[0]]),
      unit: series_by ? String(row[series_by] ?? '') : '',
    })).filter(item => item.value > 0).sort((a, b) => b.value - a.value).slice(0, 50));
  }

  get series(): ChartSeries[] {
    const { bindings, coercions } = this.data.verification;
    const categoryKey = bindings.category;
    if (!categoryKey) return [];

    const stringify = (key: string, value: unknown): string => {
      const shouldCoerce = coercions.some((coercion) => coercion.key === key);
      if (shouldCoerce || typeof value !== 'string') return String(value ?? '');
      return value;
    };

    if (bindings.series_by && bindings.series.length === 1) {
      const valueKey = bindings.series[0];
      const groups = new Map<string, ChartPoint[]>();
      this.data.result.rows.forEach((row) => {
        const name = stringify(bindings.series_by!, row[bindings.series_by!]);
        const points = groups.get(name) ?? [];
        points.push({
          category: stringify(categoryKey, row[categoryKey]),
          value: this.toNumber(row[valueKey]),
        });
        groups.set(name, points);
      });
      return Array.from(groups, ([name, points]) => ({ name, points }));
    }

    return bindings.series.map((key) => ({
      name: this.columnLabel(key),
      points: this.data.result.rows.map((row) => ({
        category: stringify(categoryKey, row[categoryKey]),
        value: this.toNumber(row[key]),
      })),
    }));
  }

  get categories(): string[] {
    const categories: string[] = [];
    this.series.forEach((series) =>
      series.points.forEach((point) => {
        if (!categories.includes(point.category)) categories.push(point.category);
      }),
    );
    return categories;
  }

  get maxValue(): number {
    return Math.max(1, ...this.series.flatMap((series) => series.points.map((point) => point.value)));
  }

  get linePaths(): Array<{ name: string; color: string; path: string; areaPath: string }> {
    const categories = this.categories;
    const plotHeight = this.chartHeight - this.topPadding - this.bottomPadding;
    return this.series.map((series, seriesIndex) => {
      const coordinates = series.points.map((point) => {
        const categoryIndex = Math.max(0, categories.indexOf(point.category));
        const x = categories.length <= 1
          ? this.chartWidth / 2
          : (categoryIndex / (categories.length - 1)) * this.chartWidth;
        const y = this.topPadding + plotHeight - (point.value / this.maxValue) * plotHeight;
        return { x, y };
      });
      const path = coordinates
        .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(1)} ${point.y.toFixed(1)}`)
        .join(' ');
      const first = coordinates[0];
      const last = coordinates[coordinates.length - 1];
      const baseline = this.topPadding + plotHeight;
      const areaPath = first && last
        ? `${path} L ${last.x.toFixed(1)} ${baseline} L ${first.x.toFixed(1)} ${baseline} Z`
        : '';
      return { name: series.name, color: this.color(seriesIndex), path, areaPath };
    });
  }

  get bars(): BarDatum[] {
    const categories = this.categories;
    const plotHeight = this.chartHeight - this.topPadding - this.bottomPadding;
    const groupWidth = this.chartWidth / Math.max(1, categories.length);
    const barWidth = Math.max(2, Math.min(42, (groupWidth * 0.72) / Math.max(1, this.series.length)));
    return this.series.flatMap((item, seriesIndex) =>
      item.points.map((point) => {
        const categoryIndex = Math.max(0, categories.indexOf(point.category));
        const height = (point.value / this.maxValue) * plotHeight;
        return {
          ...point,
          series: item.name,
          color: this.color(seriesIndex),
          x: categoryIndex * groupWidth + groupWidth * 0.14 + seriesIndex * barWidth,
          y: this.topPadding + plotHeight - height,
          width: Math.max(1, barWidth - 3),
          height,
        };
      }),
    );
  }

  get donutSlices(): DonutSlice[] {
    const points = this.series.flatMap((series) => series.points);
    const total = points.reduce((sum, point) => sum + Math.max(0, point.value), 0) || 1;
    let cursor = 0;
    return points.map((point, index) => {
      const start = cursor;
      cursor += (Math.max(0, point.value) / total) * 100;
      return { ...point, label: point.category, color: this.color(index), start, end: cursor };
    });
  }

  get donutBackground(): string {
    const stops = this.donutSlices.map((slice) => `${slice.color} ${slice.start}% ${slice.end}%`);
    return `conic-gradient(${stops.join(', ') || '#e6e8eb 0 100%'})`;
  }

  get pieLabels(): Array<{label:string; text:string; x:number; y:number}> {
    return this.donutSlices.filter(slice=>slice.end-slice.start>=3).map(slice=>{
      const angle=((slice.start+slice.end)/2)*Math.PI/50-Math.PI/2;
      const percentage=new Intl.NumberFormat(this.locale,{maximumFractionDigits:1}).format(slice.end-slice.start);
      const radius=this.visualization==='donut' ? 41 : 32;
      return {label:`${slice.label}: ${this.formatValue(slice.value)} (${percentage}%)`,
        text:`${percentage}%`,x:50+radius*Math.cos(angle),y:50+radius*Math.sin(angle)};
    });
  }

  formatValue(value: number): string {
    return new Intl.NumberFormat(this.locale, { maximumFractionDigits: 2 }).format(value);
  }

  color(index: number): string {
    return this.colors[index % this.colors.length] ?? '#206bc4';
  }

  private columnLabel(key: string): string {
    return this.data.result.columns.find((column) => column.key === key)?.label ?? key;
  }

  private toNumber(value: unknown): number {
    const parsed = typeof value === 'number' ? value : Number(value ?? 0);
    return Number.isFinite(parsed) ? parsed : 0;
  }

}
